import { SubscriptionService, useApiGetter, useRoutes, isDarkMode } from '@localstack/services';
import { EmulatorType, Permission, PlanFamily, SubscriptionStatus } from '@localstack/types';
import { styled, Typography, Link, MenuItem, Stack } from '@mui/material';
import { Dropdown, DropdownProps } from '@localstack/ui';
import { Error as ErrorIcon, ArrowDropUp } from '@mui/icons-material';
import { ReactElement, useMemo } from 'react';

import { TEST_IDS, SUB_ACTIVE_PAID_STATUSES } from '@localstack/constants';

import { formatCountdownFromTimestamp } from '~/util/timeUtils';

import { pricingRoute } from '~/util/pricing';
import { useAuthProvider } from '~/hooks/useAuthProvider';
import { LicenseAssignmentPlanLabel } from '~/components/MembersAndLicenses/LicenseAssignmentPlanLabel';

const TrialPill = styled(Link)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 1.25, 0.75, 0.75),
  borderRadius: '20px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  textDecoration: 'none',
  color: theme.palette.text.primary,
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color', 'border-color']),
  width: 'fit-content',
  '&:hover': {
    backgroundColor: isDarkMode(theme) ? theme.palette.background.paper : theme.palette.grey[200],
    borderColor: isDarkMode(theme) ? theme.palette.grey[700] : theme.palette.grey[400],
    textDecoration: 'none',
  },
}));

const StyledErrorIcon = styled(ErrorIcon)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '18px',
  display: 'flex',
  alignSelf: 'center',
}));

const TrialMenuRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
  width: '100%',
}));

const TrialMenuInfo = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const TrialCountdown = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
}));

type TrialPillLabelProps = {
  title: string;
  detail: Optional<string>;
};

const TrialPillLabel = ({ title, detail }: TrialPillLabelProps): ReactElement => (
  <Typography variant="body2" component="span" fontSize={13}>
    <strong>{title}</strong>{' '}
    <Typography component="span" variant="inherit" fontWeight={400} sx={{ opacity: 0.8 }}>
      {detail}
    </Typography>
  </Typography>
);

export const UpgradeFromTrialBanner = (): ReactElement => {
  const { can } = useAuthProvider();
  const { goto } = useRoutes();

  const { data: subscriptions } = useApiGetter(SubscriptionService, 'listSubscriptions', [], {
    suppressErrors: true,
    enable: can(Permission.READ_SUBSCRIPTIONS),
  });

  // Emulators that already have an active non-trial subscription; their trials are irrelevant.
  const emulatorsWithActiveNonTrialSub = useMemo(
    () =>
      new Set(
        (subscriptions ?? [])
          .filter((sub) => SUB_ACTIVE_PAID_STATUSES.includes(sub.status) && sub.plan?.family !== PlanFamily.TRIAL_PLANS)
          .map((sub) => sub.plan?.emulator),
      ),
    [subscriptions],
  );

  // Running trials worth showing a banner for: skip any whose emulator already has a non-trial sub (nothing to upgrade there).
  // Sorted by expiry so the most urgent trial is the one the pill counts down to and the first row in the dropdown.
  const activeTrialSubscriptions = useMemo(
    () =>
      (
        subscriptions?.filter(
          (sub) =>
            sub.plan?.family === PlanFamily.TRIAL_PLANS &&
            [SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIALING].includes(sub.status) &&
            !emulatorsWithActiveNonTrialSub.has(sub.plan?.emulator),
        ) ?? []
      ).sort((ls, rs) => (ls.cancel_at ?? Infinity) - (rs.cancel_at ?? Infinity)),
    [subscriptions, emulatorsWithActiveNonTrialSub],
  );

  const goToPricing = (emulator: EmulatorType) => goto(pricingRoute([emulator]));

  const showBanner = activeTrialSubscriptions.length > 0;

  if (!showBanner) {
    return <></>;
  }

  // Single trial: keep the compact pill.
  if (activeTrialSubscriptions.length === 1 && activeTrialSubscriptions[0]) {
    const trialSubscription = activeTrialSubscriptions[0];
    const trialType = trialSubscription.plan.emulator === EmulatorType.SNOWFLAKE ? 'Base' : 'Ultimate';

    return (
      <TrialPill data-testid={TEST_IDS.TRIAL_BANNER_PILL} onClick={() => goToPricing(trialSubscription.plan.emulator)}>
        <StyledErrorIcon />
        <TrialPillLabel
          title={`${trialType} trial expires`}
          detail={formatCountdownFromTimestamp(trialSubscription.cancel_at)}
        />
      </TrialPill>
    );
  }

  // Multiple trials: collapse into a summary pill that expands into a per-product dropdown.
  // The list is sorted by expiry, so the first entry is the one the pill counts down to.
  const soonestExpiry = activeTrialSubscriptions[0]?.cancel_at;

  return (
    <Dropdown
      placement="top-start"
      offset={[0, 3]}
      menuListProps={{ autoFocusItem: false }}
      renderButton={({ onClick }: DropdownProps) => (
        <TrialPill data-testid={TEST_IDS.TRIAL_BANNER_PILL} onClick={(event) => onClick?.(event as never)}>
          <StyledErrorIcon />
          <TrialPillLabel
            title={`${activeTrialSubscriptions.length} trials active`}
            detail={formatCountdownFromTimestamp(soonestExpiry)}
          />
          <ArrowDropUp fontSize="small" />
        </TrialPill>
      )}
    >
      {(onClose) =>
        activeTrialSubscriptions.map((sub) => (
          <MenuItem
            key={sub.id}
            data-testid={TEST_IDS.TRIAL_BANNER_MENU_ITEM(sub.plan.emulator)}
            onClick={() => {
              onClose();
              goToPricing(sub.plan.emulator);
            }}
          >
            <TrialMenuRow>
              <TrialMenuInfo>
                <LicenseAssignmentPlanLabel emulator={sub.plan.emulator} hideTier />
                <TrialCountdown>{formatCountdownFromTimestamp(sub.cancel_at)} left</TrialCountdown>
              </TrialMenuInfo>
              <Typography variant="body2" color="primary" fontWeight={600} textTransform="uppercase">
                Upgrade
              </Typography>
            </TrialMenuRow>
          </MenuItem>
        ))
      }
    </Dropdown>
  );
};
